// ═══════════════════════════════════════
// Notificaciones — campana in-app (Fase 4.5)
// ═══════════════════════════════════════
// Cada notificación es un doc en `notificaciones` con shape:
//   {
//     userId,                 // destinatario
//     tipo,                   // 'mensaje' | 'sla_vencido' | 'tarea_asignada' | 'tarea_rechazada' | ...
//     titulo, cuerpo,
//     link?,                  // ruta interna para navegar al click (e.g. '/empresas/abc?tab=mensajes')
//     recursoTipo?, recursoId?,
//     empresaId?,
//     prioridad: 'baja'|'normal'|'alta'|'critica',
//     leida: false,
//     leidaEn?: ts,
//     creadoEn, actualizadoEn,
//     metadatos?: object      // payload libre para el caller
//   }
//
// Tipos de notificación (se puede extender libremente):
//   - mensaje:         nuevo mensaje en conversación
//   - mensaje_mencion: mención explícita en un mensaje
//   - tarea_asignada:  se te asignó una tarea
//   - tarea_aceptada:  tu transferencia fue aceptada
//   - tarea_rechazada: tu transferencia fue rechazada
//   - sla_por_vencer:  falta poco para vencer
//   - sla_vencido:     el SLA venció → escalado
//   - empresa_estado:  cambió el estado de una empresa de la que dependés
//
// Uso:
//   import { notificar, marcarLeida, listarNoLeidas } from '@/lib/notificaciones'
//   await notificar({ userId, tipo:'sla_vencido', titulo, cuerpo, link, prioridad:'alta' })
// ═══════════════════════════════════════

import {
  collection, getDocs, addDoc, updateDoc, doc, serverTimestamp, query, where, orderBy, limit, writeBatch,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { forPlatformCreate, forUpdate } from './dbHelpers'

export const NOTIFICACION_TIPOS = {
  MENSAJE:           'mensaje',
  MENSAJE_MENCION:   'mensaje_mencion',
  TAREA_ASIGNADA:    'tarea_asignada',
  TAREA_ACEPTADA:    'tarea_aceptada',
  TAREA_RECHAZADA:   'tarea_rechazada',
  TAREA_VENCIDA:     'tarea_vencida',
  TAREA_RECORDATORIO:'tarea_recordatorio',
  TAREA_ESCALADA:    'tarea_escalada',
  TAREA_REASIGNADA:  'tarea_reasignada',
  TAREA_DELEGADA:    'tarea_delegada',
  SEÑAL_NUEVA:       'señal_nueva',
  SEÑAL_EXPIRADA:    'señal_expirada',
  SLA_POR_VENCER:    'sla_por_vencer',
  SLA_VENCIDO:       'sla_vencido',
  EMPRESA_ESTADO:    'empresa_estado',
  // ── Módulo Casos (B4.2, mayo 2026) ──
  CASO_ASIGNADO:           'caso_asignado',
  CASO_ACEPTADO:           'caso_aceptado',
  CASO_RECHAZADO:          'caso_rechazado',
  CASO_TRANSFERIDO:        'caso_transferido',
  CASO_ESCALADO:           'caso_escalado',
  CASO_COMENTARIO_INTERNO: 'caso_comentario_interno',
  CASO_COMENTARIO_EXTERNO: 'caso_comentario_externo',
  CASO_ADJUNTO_EXTERNO:    'caso_adjunto_externo',
  CASO_CERRADO:            'caso_cerrado',
  CASO_REABIERTO:          'caso_reabierto',
  CASO_TAREA_HIJA_CREADA:  'caso_tarea_hija_creada',
  // ── Módulo Proyectos (Bloque 13 — entregables, diseño §4.2.1) ──
  PROYECTO_ENTREGABLE_PENDIENTE: 'proyecto_entregable_pendiente',
  PROYECTO_ENTREGABLE_APROBADO:  'proyecto_entregable_aprobado',
  PROYECTO_ENTREGABLE_RECHAZADO: 'proyecto_entregable_rechazado',
  PROYECTO_HITO_LISTO:           'proyecto_hito_listo',   // todos los entregables aprobados → sugerir cumplido
  PROYECTO_HORAS_RECHAZADAS:     'proyecto_horas_rechazadas',  // Bloque 14 §12.2 — rechazo de horas vuelve con la observación
  PROYECTO_POSTMORTEM_PENDIENTE: 'proyecto_postmortem_pendiente',  // Bloque 17 §16.2 — proyecto cerrado, completar retrospectiva
  // ── Módulo Reportes congelados (PR-1b, plan Cuadro General §5 + delta §1-bis) ──
  REPORTE_PUBLICADO: 'reporte_publicado',   // al cliente: hay reporte nuevo en su portal
  REPORTE_PENDIENTE: 'reporte_pendiente',   // al consultor: un agente dejó reportes por aprobar
  REPORTE_COMENTARIO: 'reporte_comentario', // PR-1d: comentaron/respondieron en un hilo del reporte
}

export const NOTIFICACION_PRIORIDADES = {
  BAJA: 'baja',
  NORMAL: 'normal',
  ALTA: 'alta',
  CRITICA: 'critica',
}

/**
 * Crea una notificación para un usuario.
 * @returns {Promise<{id:string}>}
 */
export async function notificar({
  userId,
  tipo,
  titulo,
  cuerpo = '',
  link = null,
  recursoTipo = null,
  recursoId = null,
  empresaId = null,
  prioridad = NOTIFICACION_PRIORIDADES.NORMAL,
  metadatos = null,
}) {
  if (!userId) throw new Error('userId requerido')
  if (!tipo) throw new Error('tipo requerido')
  if (!titulo) throw new Error('titulo requerido')
  const payload = forPlatformCreate({
    userId,
    tipo,
    titulo,
    cuerpo,
    link,
    recursoTipo,
    recursoId,
    empresaId,
    prioridad,
    leida: false,
    leidaEn: null,
    metadatos,
  })
  const ref = await addDoc(collection(db, COLLECTIONS.NOTIFICACIONES), payload)
  return { id: ref.id }
}

/**
 * Notificar a varios usuarios en batch.
 * Los dedupea si se repite userId.
 */
export async function notificarVarios(userIds, datosSinUserId) {
  const únicos = Array.from(new Set(userIds.filter(Boolean)))
  const ids = []
  for (const uid of únicos) {
    const r = await notificar({ ...datosSinUserId, userId: uid })
    ids.push(r.id)
  }
  return ids
}

// ═══════════════════════════════════════════════════════════════════════════
// A dónde navega el click de una notificación (report YOmfntELsiWWS5fscelq)
// ═══════════════════════════════════════════════════════════════════════════
//
// EL LINK ES UN STRING PERSISTIDO, Y LOS VIEJOS QUEDARON INCOMPLETOS. El fix
// del report c3mwYtc8Q8vds7ZkyWru arregló al ESCRITOR: desde entonces
// `linkTareaNotificacion` (functions/lib/tareaLinkNotificacion.js) emite
// `/asignaciones/{id}?empresa={empresaId}`, porque la pantalla de detalle
// necesita saber de qué empresa leer y su fallback —la primera empresa del
// VIEWER— acierta sólo por casualidad. Pero las notificaciones escritas ANTES
// siguen guardadas con el link pelado, y cada una es un callejón sin salida
// para su destinatario: staff sin membresía en la empresa dueña ve
// "No pudimos abrir esta tarea".
//
// EL DATO NO FALTA: ESTÁ AL LADO. Todo escritor de notificaciones guarda
// `empresaId` como campo del doc —`notificar()` acá arriba lo declara, y los
// seis escritores del backend lo escriben en la línea siguiente al `link`
// (functions/asignaciones.js:151, :214, :257, :273, :311 ·
// functions/migraciones.js:297). Quien navega tiene el doc entero en la mano y
// usaba sólo `link`. Es el MISMO defecto que c3mwYtc8Q8vds7ZkyWru, del otro
// lado del cable: allá el escritor tiraba el dato, acá el lector.
//
// POR QUÉ SE REPARA AL LEER Y NO SE MIGRAN LOS DOCS: reparar en lectura es
// retroactivo por construcción —vale para todo doc viejo, sin tocar datos— y
// una migración sobre las notificaciones de todos los tenants es pólvora que
// no compra nada más. Precepto de una sola fuente: el `empresaId` del doc ES
// el dato; el `empresaIds[0]` del viewer es una adivinanza.
//
// POR QUÉ ES GENÉRICO Y NO MIRA LA RUTA. Esta es la horizontal de
// notificaciones: no puede conocer las rutas de un vertical (`/asignaciones`
// es del módulo Tareas, y la plataforma nunca depende de un vertical). La
// regla que aplica no necesita saberlo: `empresa` es el param con el que este
// repo dice "de qué empresa habla este link", y `n.empresaId` es exactamente
// eso para la notificación. Tampoco compone la forma del link a tarea —sólo
// agrega el param que falta—, así que NO es una tercera copia de ese shape
// (las dos que hay, front y back, tienen test de paridad entre sí).
//
// FAIL-CLOSED: si el link no es una ruta interna, si ya trae `empresa`, o si
// el doc no tiene `empresaId`, se devuelve TAL CUAL. `//host` se descarta a
// propósito aunque empiece con `/`: es una URL protocol-relative, no una ruta.
const BASE_INTERNA = 'https://interna.invalid'

/**
 * Ruta a la que navega el click de una notificación.
 *
 * @param {{link?: string, empresaId?: string}} n — el doc de notificación.
 * @returns {string|null} la ruta a navegar, o `null` si la notificación no
 *   tiene link (el llamador no navega).
 */
export function destinoDeNotificacion(n) {
  const link = typeof n?.link === 'string' ? n.link : null
  if (!link) return null
  if (!link.startsWith('/') || link.startsWith('//')) return link
  const empresaId = n?.empresaId
  if (!empresaId) return link
  const u = new URL(link, BASE_INTERNA)
  if (u.searchParams.has('empresa')) return link
  u.searchParams.set('empresa', empresaId)
  return `${u.pathname}${u.search}${u.hash}`
}

export async function marcarLeida(notifId) {
  await updateDoc(doc(db, COLLECTIONS.NOTIFICACIONES, notifId), forUpdate({
    leida: true,
    leidaEn: serverTimestamp(),
  }))
}

export async function marcarTodasLeidas(userId) {
  const q = query(
    collection(db, COLLECTIONS.NOTIFICACIONES),
    where('userId', '==', userId),
    where('leida', '==', false),
  )
  const snap = await getDocs(q)
  if (snap.empty) return 0
  const batch = writeBatch(db)
  snap.docs.forEach(d => batch.update(d.ref, forUpdate({
    leida: true, leidaEn: serverTimestamp(),
  })))
  await batch.commit()
  return snap.size
}

/**
 * Listar notificaciones no leídas de un usuario, más recientes primero.
 */
export async function listarNoLeidas(userId, { max = 50 } = {}) {
  const q = query(
    collection(db, COLLECTIONS.NOTIFICACIONES),
    where('userId', '==', userId),
    where('leida', '==', false),
    orderBy('creadoEn', 'desc'),
    limit(max),
  )
  const snap = await getDocs(q)
  return snap.docs.map(docConId)
}

/**
 * Listar todas (leídas y no) las N más recientes.
 */
export async function listarRecientes(userId, { max = 30 } = {}) {
  const q = query(
    collection(db, COLLECTIONS.NOTIFICACIONES),
    where('userId', '==', userId),
    orderBy('creadoEn', 'desc'),
    limit(max),
  )
  const snap = await getDocs(q)
  return snap.docs.map(docConId)
}

// Exposición en window
if (typeof window !== 'undefined') {
  window.__notificar = notificar
  window.__listarNoLeidas = listarNoLeidas
}
